import React, { useState, useContext, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { JobSearchContext } from '../../context';
import FilterList from '../filter_list/filter_list';
import Alerts from '../Alerts/alerts';
import Header from '../header/header';
import Offcanvas from 'react-bootstrap/Offcanvas';
import SortingFilter from '../sorting_filter/sorting_filter';
import {
  JOB_POSTING_OPTIONS_PATH,
  MY_POSTED_JOBS,
  MY_SAVED_JOBS,
  RECRUITER_DASHBOARD_MY_POSTED_JOBS,
} from '../../constants/job_search.constants';
import MobileKeywordsFilter from '../mobile_keywords_filter/mobile_keywords_filter';
import MobileFilters from '../mobile_filters/mobile_filters';
import ResultSection from '../result_section/result_section';
import Footer from '../footer/footer';
import ResultInformation from '../result_information/result_information';
import SmartFilters from '../smart_filters/smart_filters';
import DidYouMean from '../did_you_mean/did_you_mean';
import TopEmployers from '../top_employers/top_employers';
import AdButler from '../../../components/react/adbutler/adbutler';
import LandingPagesVertical from '../../../landing_pages_vertical/landing_pages_vertical';
import { getCareerHubNewsletter } from '../../services/job_search.service';
import styles from './container.module.scss';

const Container = () => {
  const containerRef = useRef(null);
  const [showOffCanvas, setShowOffCanvas] = useState(false);

  const {
    deviceInfo,
    userInfo,
    adbutlerInfo,
    featuredJobs,
    scrollToElement,
    keywordsRef,
    landingPageInfo: { topEmployers },
  } = useContext(JobSearchContext);

  const { isSignedIn, hasPostedJobs, viewRecruiterTools } = userInfo;
  const isDesktop = !(deviceInfo.isMobile || deviceInfo.isTablet);
  const [careerHubNewsletter, setCareerHubNewsletter] = useState(null);
  const myPostedJobsURL = viewRecruiterTools ? RECRUITER_DASHBOARD_MY_POSTED_JOBS : MY_POSTED_JOBS;

  useEffect(() => {
    getCareerHubNewsletter().then((response) => {
      setCareerHubNewsletter(response);
    });
  }, []);

  const renderHowToFeatureJob = (
    <a
      className='small link-blue-orange'
      target='_blank'
      href='https://devex.zendesk.com/hc/en-us/articles/360000105614-Featuring-jobs'
      rel='noreferrer'
    >
      How do I <strong>feature my job</strong> here?
    </a>
  );

  const renderUserJobsLinks = (
    <div
      id='user-jobs-links'
      className={classNames(`${styles.userJobsLinks} d-grid gap-3 px-3`, {
        [styles.visible]: isSignedIn,
        'mt-3': isSignedIn,
      })}
    >
      {hasPostedJobs && (
        <a
          className='btn btn-outline-info strong'
          data-testid='my-posted-jobs-link'
          href={myPostedJobsURL}
        >
          <i className='fal icon-briefcase pe-2' />
          My posted jobs
        </a>
      )}
      <a
        className='btn btn-outline-info strong'
        data-testid='my-saved-jobs-link'
        href={MY_SAVED_JOBS}
      >
        <i className='fal icon-star pe-2' />
        My saved jobs
      </a>
    </div>
  );

  const renderPostAJob = (
    <div className={`px-lg-3 mb-2 mb-lg-0 mt-2 d-grid ${styles.postAJob}`}>
      <a
        className='btn btn-dark'
        data-testid='post-a-job-link'
        href={JOB_POSTING_OPTIONS_PATH}
        target='_blank'
        rel='noreferrer'
      >
        <div className='row py-1 px-2'>
          <div className='col-10 d-grid text-start'>
            <span>Do you have an open position?</span>
            <strong>Post a job now!</strong>
          </div>
          <div className='col-2'>
            <i className='fal icon-arrow-circle-right' />
          </div>
        </div>
      </a>
    </div>
  );

  const renderDesktopSidebar = (
    <>
      {renderPostAJob}
      {renderUserJobsLinks}
      <AdButler
        adbutlerInfo={{ ...adbutlerInfo, zone: adbutlerInfo.sidebarZone, place: 0 }}
        isSignedIn={isSignedIn}
      />
      {!!topEmployers.length && <TopEmployers />}
      {!!careerHubNewsletter && (
        <LandingPagesVertical newsletter={careerHubNewsletter} isSignedIn={isSignedIn} />
      )}
      <AdButler
        adbutlerInfo={{ ...adbutlerInfo, zone: adbutlerInfo.sidebarZone, place: 1 }}
        isSignedIn={isSignedIn}
      />
    </>
  );

  /* eslint-disable max-len */
  const mobileVersion = (
    <>
      <div className={`${styles.jobSearch}} my-3`}>
        <Header />
        <div>
          <div className={`d-flex justify-content-between align-items-center ${styles.jobsSearchTitle}`}>
            <h1 className='mb-0 py-1' data-testid='jobs-search-title'>Find a Job</h1>
            <i
              data-testid='jobs-open-offcanvas'
              className='fal icon-ellipsis-v'
              onClick={() => setShowOffCanvas(!showOffCanvas)}
            />
          </div>
          <div ref={scrollToElement} />
          <SmartFilters />
          <div className='mt-2' ref={containerRef}>
            <div className='mt-0 px-0'>
              <MobileKeywordsFilter containerRef={containerRef} />
              <div
                className='pb-3 px-28'
                style={{
                  paddingTop: keywordsRef.current?.offsetHeight || 0,
                }}
              >
                <DidYouMean />
                <div className='row pt-3 position-relative'>
                  <div className='col-12 d-flex mb-3 justify-content-center align-items-center'>
                    {Boolean(featuredJobs.length) && renderHowToFeatureJob}
                  </div>
                  <div className='col-12 d-flex justify-content-center align-items-center'>
                    <ResultInformation />
                    <div className='mx-2 align-self-baseline'>.</div>
                    <SortingFilter />
                  </div>
                </div>
              </div>
              <div className='px-28'>
                <ResultSection />
                <Footer containerRef={containerRef} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <MobileFilters />
      <Offcanvas
        show={showOffCanvas}
        placement='bottom'
        onHide={() => {
          setShowOffCanvas(!showOffCanvas);
        }}
        className={styles.offCanvas}
        data-testid='jobs-offcanvas'
      >
        <Offcanvas.Body>
          <div
            className={`d-flex flex-column justify-content-between px-1 py-3 ${styles.offCanvasBody}`}
          >
            <a
              data-testid='post-a-job-link-mobile'
              href={JOB_POSTING_OPTIONS_PATH}
              target='_blank'
              className={classNames('btn btn-dark', { 'mb-3': isSignedIn })}
              rel='noreferrer'
            >
              Post a job now! <i className='fal icon-arrow-circle-right' />
            </a>
            {isSignedIn && (
              <>
                {hasPostedJobs && (
                  <a
                    data-testid='my-posted-jobs-link-mobile'
                    href={myPostedJobsURL}
                    className='fw-bold btn btn-outline-info mb-3'
                  >
                    <i className='fal icon-suitcase' /> My posted jobs
                  </a>
                )}
                <a
                  data-testid='my-saved-jobs-link-mobile'
                  href={MY_SAVED_JOBS}
                  className='fw-bold btn btn-outline-info'
                >
                  <i className='fal icon-star' /> My saved jobs
                </a>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );

  const desktopVersion = (
    <div className={`${styles.jobSearch} my-45 position-relative`}>
      <Header />
      <div>
        <h1 className='py-1 border-bottom' data-testid='jobs-search-title'>
          Find a Job
        </h1>
        <SmartFilters />
        <div className='row mt-1' ref={containerRef}>
          <div className='col-8 main mt-4'>
            <div className='pt-3 border rounded-1' data-testid='filters-and-alerts'>
              <FilterList />
              {isSignedIn && <Alerts />}
            </div>
            <div className='pt-4 pb-3'>
              <DidYouMean />
              <div className='row'>
                <div className='col-5 d-flex mb-0 justify-content-start align-items-center'>
                  {Boolean(featuredJobs.length) && renderHowToFeatureJob}
                </div>
                <div className='col-7 d-flex justify-content-end align-items-center'>
                  <ResultInformation />
                  <div className='mx-2 align-self-baseline'>.</div>
                  <SortingFilter />
                </div>
              </div>
            </div>
            <ResultSection />
            <Footer containerRef={containerRef} />
          </div>
          <div className='col-4 pt-3 pe-0 px-4'>
            <div data-testid='sidebar'>{renderDesktopSidebar}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isDesktop) return mobileVersion;
  return desktopVersion;
};

export default Container;
